/**
 * ThemeContext — owns the live theme state for the app.
 *
 * Single source of truth: the admin's saved org theme (Appearance in
 * /admin). No user-level toggle override — the admin's choice always
 * wins so brand consistency stays intact across the team. If a user
 * wants their own preference, that's a feature for a future session-
 * level setting; for now, what the admin saved is what everyone sees.
 *
 * Brand tokens are applied at runtime by writing CSS variables to
 * `:root`, so admin changes flow through immediately without a CSS
 * rebuild. Light/dark mode is controlled by toggling the `light` class
 * on the document root (the dark default lives on :root).
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api } from '@/lib/api'

export type Mode = 'dark' | 'light'
export type ModePreference = Mode | 'system'

export interface BrandPalette {
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string
  600: string
  700: string
  800: string
  900: string
}

export interface ThemeConfig {
  preset: string
  /** Org-level mode default. User local override (`forge-theme` in
   *  localStorage) wins for the active session. */
  mode: ModePreference
  brand: BrandPalette
  density?: 'compact' | 'normal'
}

// ── Preset palettes ──

export const PRESETS: Record<string, BrandPalette & { label: string }> = {
  forge: {
    label: 'Forge (emerald)',
    50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399',
    500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b',
  },
  amber: {
    label: 'Amber',
    50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24',
    500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f',
  },
  navy: {
    label: 'Navy blue',
    50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa',
    500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a',
  },
  violet: {
    label: 'Violet',
    50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd', 400: '#a78bfa',
    500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6', 900: '#4c1d95',
  },
  crimson: {
    label: 'Crimson',
    50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5', 400: '#f87171',
    500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b', 900: '#7f1d1d',
  },
  slate: {
    label: 'Slate (monochrome)',
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8',
    500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a',
  },
}

const DEFAULT_THEME: ThemeConfig = {
  preset: 'forge',
  mode: 'dark',
  brand: PRESETS.forge,
  density: 'normal',
}

interface ThemeContextValue {
  /** Live mode resolved from the org default (and system pref when
   *  org mode = 'system'). No user override layer. */
  theme: Mode
  /** Org-level config (used by the settings page editor). */
  config: ThemeConfig
  /** True while the org config is being fetched on first load. */
  loading: boolean
  /** Kept on the API for backward compat with old menu items —
   *  no-ops now since users can't override the org theme. */
  toggleTheme: () => void
  /** Apply a config locally (preview, before save). Doesn't persist. */
  previewConfig: (next: Partial<ThemeConfig>) => void
  /** Persist org config — admin only. */
  saveConfig: (next: ThemeConfig) => Promise<void>
  /** Reset preview back to the saved org config. */
  resetPreview: () => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

// Best-effort cleanup of any leftover user override from earlier
// versions, so users don't stay stuck on a mode the admin no longer
// uses. Safe to remove after a few release cycles.
const LEGACY_USER_OVERRIDE_KEY = 'forge-theme'

function applyBrandToDom(brand: BrandPalette) {
  const root = document.documentElement
  ;(Object.entries(brand) as unknown as Array<[keyof BrandPalette, string]>).forEach(([shade, hex]) => {
    root.style.setProperty(`--brand-${shade}`, hex)
  })
}

function resolveMode(pref: ModePreference): Mode {
  if (pref === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light'
    }
    return 'dark'
  }
  return pref
}

function applyModeToDom(mode: Mode) {
  const root = document.documentElement
  root.classList.remove('dark', 'light')
  root.classList.add(mode)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Saved org config (last fetched from API). Local previews don't replace this.
  const [savedConfig, setSavedConfig] = useState<ThemeConfig>(DEFAULT_THEME)
  // Active config — what's currently applied to the DOM. Starts at saved,
  // mutated by previewConfig for live editing in the Appearance editor.
  const [config, setConfig] = useState<ThemeConfig>(DEFAULT_THEME)
  const [loading, setLoading] = useState(true)

  // Strip any legacy user-override key on mount — the admin's saved
  // theme is now the single source of truth.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(LEGACY_USER_OVERRIDE_KEY)
    }
  }, [])

  const theme = resolveMode(config.mode)

  // Apply mode + brand whenever they change.
  useEffect(() => {
    applyModeToDom(theme)
  }, [theme])

  useEffect(() => {
    applyBrandToDom(config.brand)
  }, [config.brand])

  // Load theme on mount. Tries the authed org endpoint first; falls
  // back to the public endpoint when no JWT is present (landing, signin,
  // signup) so unauthenticated visitors still see the platform brand.
  useEffect(() => {
    let cancelled = false
    const fetchTheme = async () => {
      const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('forge-token')
      try {
        const res = hasToken ? await api.getOrgTheme() : await api.getPublicTheme()
        if (cancelled) return
        if (res?.theme) {
          const merged = mergeWithDefault(res.theme as Partial<ThemeConfig>)
          setSavedConfig(merged)
          setConfig(merged)
        }
      } catch {
        // Auth failed (token expired, etc.) — try the public endpoint as
        // a last resort so visitors still get the brand.
        try {
          const pub = await api.getPublicTheme()
          if (!cancelled && pub?.theme) {
            const merged = mergeWithDefault(pub.theme as Partial<ThemeConfig>)
            setSavedConfig(merged)
            setConfig(merged)
          }
        } catch { /* fall through to defaults */ }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void fetchTheme()
    return () => { cancelled = true }
  }, [])

  // React to system mode changes when org pref is 'system'.
  useEffect(() => {
    if (config.mode !== 'system') return
    const mql = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => applyModeToDom(mql.matches ? 'light' : 'dark')
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [config.mode])

  // No-op kept for backward compat with old menu items that called it.
  // Theme is admin-only now.
  const toggleTheme = useCallback(() => { /* admin-controlled — see /admin → Appearance */ }, [])

  const previewConfig = useCallback((next: Partial<ThemeConfig>) => {
    setConfig((prev) => ({ ...prev, ...next, brand: next.brand ?? prev.brand }))
  }, [])

  const resetPreview = useCallback(() => {
    setConfig(savedConfig)
  }, [savedConfig])

  const saveConfig = useCallback(async (next: ThemeConfig) => {
    await api.updateOrgTheme(next as unknown as Record<string, unknown>)
    setSavedConfig(next)
    setConfig(next)
  }, [])

  return (
    <ThemeContext.Provider
      value={{ theme, config, loading, toggleTheme, previewConfig, saveConfig, resetPreview }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

function mergeWithDefault(partial: Partial<ThemeConfig>): ThemeConfig {
  // 'custom' is a valid preset key even though it isn't in PRESETS — it
  // means "use the brand palette stored on the org, not a built-in".
  const isCustom = partial.preset === 'custom'
  const presetKey = isCustom
    ? 'custom'
    : partial.preset && PRESETS[partial.preset] ? partial.preset : 'forge'
  const baseBrand = isCustom
    ? (partial.brand ?? PRESETS.forge)
    : PRESETS[presetKey]
  return {
    preset: presetKey,
    mode: partial.mode ?? 'dark',
    brand: { ...baseBrand, ...(partial.brand ?? {}) } as BrandPalette,
    density: partial.density ?? 'normal',
  }
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
